import { useTranslation } from 'react-i18next';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useActivateDeactivateProduct } from 'api/services/productsService';
import { Product } from 'types/productsTypes';
import { ErrorType } from 'types/genericTypes';
import { useCallback } from 'react';
import { GenericDeleteModal } from 'components/ui/GenericDeleteModal';

interface ActivateDeactivateProductProps {
  open: boolean;
  onCancel: () => void;
  product?: Product;
  selectedCompany?: string;
}

export const ActivateDeactivateProduct = ({
  open,
  onCancel,
  product,
  selectedCompany,
}: ActivateDeactivateProductProps) => {
  const { t } = useTranslation();
  const {
    mutateAsync: activateDesactivateProduct,
    isPending: isPendingActivateDesactivateProduct,
  } = useActivateDeactivateProduct();
  const { openNotification } = useFloatNotification();

  const handleActivateDeactivateProduct = useCallback(async () => {
    const successActiveDeactvie = product?.active
      ? t('product.activate-deactivate.disable')
      : t('product.activate-deactivate.activate');
    const errorActiveDeactvie = product?.active
      ? t('product.activate-deactivate.error-disable')
      : t('product.activate-deactivate.error-activate');

    try {
      await activateDesactivateProduct({
        companyId: selectedCompany || '',
        productId: product?.id || '',
        payload: {
          active: !product?.active,
        },
      });

      openNotification(
        t('success'),
        t('product.activate-deactivate.success-notification-message', {
          active: successActiveDeactvie,
        }),
        'success'
      );
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('product.activate-deactivate.error-notification-message', {
            active: errorActiveDeactvie,
          }),
          'error'
        );
      }
    }

    onCancel();
  }, [
    product?.active,
    product?.id,
    selectedCompany,
    t,
    activateDesactivateProduct,
    openNotification,
    onCancel,
  ]);

  return (
    <GenericDeleteModal
      open={open}
      onCancel={onCancel}
      onOk={handleActivateDeactivateProduct}
      confirmLoading={isPendingActivateDesactivateProduct}
      okTitle={t('product.activate-deactivate-modal.confirm-button')}
      bodyText={
        product?.active
          ? t('product.activate-deactivate-modal.body-deactivation')
          : t('product.activate-deactivate-modal.body-activation')
      }
    />
  );
};
